import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { GoStar, GoStarFill } from 'react-icons/go';
import { MdEdit } from 'react-icons/md';
import SelectDropDown from '@/components/UI/SelectDropDown';
import MenuContainer from '@/components/UI/SelectDropDown/Menus/MenuContainer';
import Button from '@/components/UI/Button/Button';
import Spinner from '@/components/UI/Spinner';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';
import { _isNotEmpty, _notNil } from '@/littledash';
import { useModalAction } from '@/utils/modal';
import { canEditView, partitionViews, savingBlockedReason } from '@/utils/userTableSettings/UserTableSettings.utils';
import * as Auth from '@/support/auth';
import { isMacPlatform } from '@/utils/browser.utils';
import { errorToast, successToast } from '@/helpers';
import styles from './ViewSelector.module.scss';
import type {
  UserTableView,
  UserTableViewApiId,
  UserTableViewSettings,
  UserTableViewTableName,
} from '@/model/UserTableView.model.ts';

interface ViewSelectorProps {
  tableName: UserTableViewTableName;
  /** Callback to get current table settings when saving a new view */
  getCurrentSettings: () => UserTableViewSettings;
  /** Called when user selects a different view */
  onViewChange: (settings: UserTableViewSettings | undefined) => void;
}

const DEFAULT_STAR_KEY = 'default';

/**
 * Identity token per mounted selector. The Cmd/Ctrl+S shortcut is global, so it needs to know
 * whether the page holds exactly one table before acting on "the" selected view.
 */
const mountedSelectors = new Set<object>();

interface ActiveStarProps {
  isActive: boolean;
  isBusy: boolean;
  label: string;
  testId: string;
  onClick: () => void;
}

const ActiveStar: FC<ActiveStarProps> = ({ isActive, isBusy, label, testId, onClick }) => (
  <div
    className={`${styles.starButton} ${isActive ? styles.starred : ''} ${isBusy ? styles.disabled : ''}`}
    onClick={onClick}
    data-testid={testId}
    aria-label={label}
  >
    {isBusy ? <Spinner size="small" color="gray" /> : isActive ? <GoStarFill size={16} /> : <GoStar size={16} />}
  </div>
);

interface ViewSelectorMenuProps {
  tableName: UserTableViewTableName;
  onViewChange: (settings: UserTableViewSettings | undefined) => void;
  /**
   * Owned by ViewSelector so the keyboard shortcut and this button take the same path. Resolves
   * false when the save failed, so the menu can stay open for a retry.
   */
  onSaveChanges: () => Promise<boolean>;
  onSaveNewView: () => void;
  isSavingChanges: boolean;
  closeMenu?: () => void;
}

const ViewSelectorMenu: FC<ViewSelectorMenuProps> = ({
  tableName,
  onViewChange,
  onSaveChanges,
  onSaveNewView,
  isSavingChanges,
  closeMenu,
}) => {
  const { getViews, getActiveViewApiId, getSelectedViewApiId, selectView, setActiveView, clearActiveView } =
    useUserTableViews();
  const { openModal } = useModalAction();
  const [isSettingActive, setIsSettingActive] = useState<string | null>(null);

  const views = getViews(tableName);
  const { published: publishedViews, personal: personalViews } = partitionViews(views);
  const activeViewApiId = getActiveViewApiId(tableName);
  const selectedViewApiId = getSelectedViewApiId(tableName);
  const isTenantAdmin = Auth.isCurrentTeamOwner() === true;

  const handleSelectDefault = () => {
    selectView(tableName, undefined);
    onViewChange(undefined);
    closeMenu?.();
  };

  const handleSelectView = (view: UserTableView) => {
    selectView(tableName, view.api_id);
    onViewChange(view.settings);
    closeMenu?.();
  };

  const handleSetActive = async (view: UserTableView | undefined) => {
    if (isSettingActive !== null || view?.api_id === activeViewApiId) {
      return;
    }
    setIsSettingActive(view?.api_id ?? DEFAULT_STAR_KEY);
    const success = _notNil(view) ? await setActiveView(tableName, view.api_id) : await clearActiveView(tableName);
    setIsSettingActive(null);
    if (!success) {
      errorToast('Failed to set active view. Please try again.');
    }
  };

  const handleEditView = (view: UserTableView) => {
    closeMenu?.();
    openModal('EDIT_TABLE_VIEW', {
      tableName,
      view,
      onViewDeleted: (deletedApiId: UserTableViewApiId) => {
        // Only the view on screen needs the table reset; deleting any other leaves it untouched.
        if (deletedApiId === selectedViewApiId) {
          selectView(tableName, undefined);
          onViewChange(undefined);
        }
      },
    });
  };

  const handleSaveNewView = () => {
    closeMenu?.();
    onSaveNewView();
  };

  const handleSaveChanges = async () => {
    if (await onSaveChanges()) {
      closeMenu?.();
    }
  };

  const limitReason = savingBlockedReason(views, isTenantAdmin);
  const selectedView = views.find((view) => view.api_id === selectedViewApiId);
  const hasSelectedView = selectedViewApiId !== undefined;
  const selectedViewReadOnly = _notNil(selectedView) && !canEditView(selectedView, isTenantAdmin);
  // Modifier wording follows isMacPlatform, as the rest of the app labels shortcuts. The disabled
  // reason wins the tooltip when there is one - the shortcut is only reachable while enabled anyway.
  const modifier = isMacPlatform() === true ? 'Cmd' : 'Ctrl';
  const saveChangesReason = !hasSelectedView
    ? 'Select a saved view to save changes to'
    : selectedViewReadOnly
      ? 'Only an admin can change a published view'
      : undefined;

  const renderViewRow = (view: UserTableView) => (
    <div key={view.api_id} className={`${styles.viewItem} ${view.api_id === selectedViewApiId ? styles.active : ''}`}>
      <ActiveStar
        isActive={view.api_id === activeViewApiId}
        isBusy={isSettingActive === view.api_id}
        label={view.api_id === activeViewApiId ? `${view.name} is your active view` : `Set ${view.name} as active view`}
        testId={`view-selector__star-${view.api_id}`}
        onClick={() => handleSetActive(view)}
      />
      <div
        className={styles.viewSelect}
        onClick={() => handleSelectView(view)}
        data-testid={`view-selector__view-${view.api_id}`}
      >
        <span className={styles.viewName}>{view.name}</span>
      </div>
      <div className={styles.viewActions}>
        {canEditView(view, isTenantAdmin) && (
          <div
            className={styles.editButton}
            onClick={() => handleEditView(view)}
            data-testid={`view-selector__edit-${view.api_id}`}
            aria-label={`Edit ${view.name}`}
          >
            <MdEdit size={16} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <MenuContainer width={400}>
      <div className={styles.header}>Saved Views</div>
      <div className={styles.viewList}>
        <div className={`${styles.viewItem} ${selectedViewApiId === undefined ? styles.active : ''}`}>
          <ActiveStar
            isActive={activeViewApiId === undefined}
            isBusy={isSettingActive === DEFAULT_STAR_KEY}
            label={activeViewApiId === undefined ? 'Default is your active view' : 'Set Default as active view'}
            testId="view-selector__star-default"
            onClick={() => handleSetActive(undefined)}
          />
          <div className={styles.viewSelect} onClick={handleSelectDefault} data-testid="view-selector__default">
            <span className={styles.viewName}>Default</span>
          </div>
        </div>

        {_isNotEmpty(publishedViews) && (
          <>
            <div className={styles.groupDivider} data-testid="view-selector__divider-team">
              Team views
            </div>
            {publishedViews.map(renderViewRow)}
          </>
        )}

        {_isNotEmpty(publishedViews) && _isNotEmpty(personalViews) && (
          <div className={styles.groupDivider} data-testid="view-selector__divider-personal">
            My views
          </div>
        )}

        {personalViews.map(renderViewRow)}
      </div>

      <div className={styles.saveSection}>
        <Button
          onClick={handleSaveNewView}
          className="w-100 f6"
          plain
          testId="view-selector__save-button"
          disabled={_notNil(limitReason)}
          tooltip={limitReason ?? `${modifier} + Shift + S`}
        >
          Save new
        </Button>
        <Button
          onClick={handleSaveChanges}
          className="w-100 f6"
          plain
          testId="view-selector__save-changes-button"
          disabled={_notNil(saveChangesReason)}
          loading={isSavingChanges}
          tooltip={saveChangesReason ?? `${modifier} + S`}
        >
          Save changes
        </Button>
      </div>
    </MenuContainer>
  );
};

const ViewSelector: FC<ViewSelectorProps> = ({ tableName, getCurrentSettings, onViewChange }) => {
  const { getViews, getActiveViewApiId, getSelectedViewApiId, selectView, updateViewSettings, isLoaded } =
    useUserTableViews();
  const { openModal } = useModalAction();
  const isModalOpen = useSelector((state: { ui?: { modal?: { open?: boolean } } }) => state.ui?.modal?.open ?? false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  /**
   * APITable re-reads getActiveSettings every time it mounts, so a table always comes back showing
   * its active view. The selection has to be reset to match: the provider seeds on load, but it
   * deliberately leaves an existing selection alone, which would otherwise leave the trigger naming
   * the view selected before navigating away while the table shows the active one's settings.
   */
  const seededTable = useRef<UserTableViewTableName | null>(null);
  useEffect(() => {
    if (isLoaded && seededTable.current !== tableName) {
      seededTable.current = tableName;
      selectView(tableName, getActiveViewApiId(tableName));
    }
  }, [isLoaded, tableName, getActiveViewApiId, selectView]);

  const views = getViews(tableName);
  const selectedViewApiId = getSelectedViewApiId(tableName);
  const selectedView = views.find((v) => v.api_id === selectedViewApiId);
  const displayName = selectedView?.name ?? 'Default';
  const isTenantAdmin = Auth.isCurrentTeamOwner() === true;
  const selectedViewReadOnly = _notNil(selectedView) && !canEditView(selectedView, isTenantAdmin);
  const limitReason = savingBlockedReason(views, isTenantAdmin);

  const saveNewView = useCallback(() => {
    openModal('SAVE_TABLE_VIEW', {
      tableName,
      settings: getCurrentSettings(),
      onViewSaved: onViewChange,
    });
  }, [tableName, getCurrentSettings, onViewChange, openModal]);

  const saveChanges = useCallback(async (): Promise<boolean> => {
    if (selectedViewApiId === undefined || isSavingChanges) {
      return false;
    }
    // The keyboard shortcut reaches this without passing the button's disabled state.
    if (selectedViewReadOnly) {
      errorToast('Only an admin can change a published view');
      return false;
    }
    setIsSavingChanges(true);
    const selectedViewName = views.find((view) => view.api_id === selectedViewApiId)?.name;
    const success = await updateViewSettings(tableName, selectedViewApiId, getCurrentSettings());
    setIsSavingChanges(false);
    if (success) {
      successToast(_notNil(selectedViewName) ? `Table view '${selectedViewName}' updated` : 'Table view updated');
    } else {
      errorToast('Failed to save changes. Please try again.');
    }
    return success;
  }, [
    selectedViewApiId,
    isSavingChanges,
    selectedViewReadOnly,
    views,
    tableName,
    getCurrentSettings,
    updateViewSettings,
  ]);

  /**
   * Save / Save As, on the table's views:
   *
   * <ul>
   *   <li>Cmd/Ctrl+S updates the selected view, or opens the save modal when on Default
   *   <li>Cmd/Ctrl+Shift+S always opens the save modal, so a new view can be branched off the one
   *       being shown without overwriting it - the only path that has no undo
   * </ul>
   *
   * `event.code` rather than `event.key`, because Shift turns `key` into 'S' and the code survives
   * non-QWERTY layouts. Note `main.tsx` only suppresses the browser dialog for the unshifted
   * combination, so the preventDefault below is what covers the Shift variant.
   */
  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    const token = {};
    mountedSelectors.add(token);

    const listener = (event: KeyboardEvent) => {
      if (event.code !== 'KeyS' || !(event.metaKey || event.ctrlKey)) {
        return;
      }
      // A modal owns the keyboard while it is open, and with more than one table on the page there
      // is no way to tell which one the user meant - better to do nothing than guess.
      if (isModalOpen || mountedSelectors.size !== 1) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();

      // Shift forces a new view; without it, Default is the only state with nothing to update.
      if (!event.shiftKey && selectedViewApiId !== undefined) {
        void saveChanges();
      } else if (_notNil(limitReason)) {
        errorToast(limitReason);
      } else {
        saveNewView();
      }
    };

    document.body.addEventListener('keydown', listener);
    return () => {
      document.body.removeEventListener('keydown', listener);
      mountedSelectors.delete(token);
    };
  }, [isLoaded, isModalOpen, selectedViewApiId, limitReason, saveChanges, saveNewView]);

  if (!isLoaded) {
    return null;
  }

  return (
    <SelectDropDown
      title={displayName}
      className={`plain f6 ${styles.trigger}`}
      alignMenu="right"
      testId="view-selector"
    >
      <ViewSelectorMenu
        tableName={tableName}
        onViewChange={onViewChange}
        onSaveChanges={saveChanges}
        onSaveNewView={saveNewView}
        isSavingChanges={isSavingChanges}
      />
    </SelectDropDown>
  );
};

export default ViewSelector;
